import { useMemo, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useAssets,
  useCreateAsset,
  type AssetFilters,
} from '../api/queries/assets';
import { ApiError } from '../api/client';
import { can } from '../auth/store';
import { toast } from '../components/Toasts';
import {
  Badge,
  Button,
  Dialog,
  FormField,
  FormRow,
  Input,
  KpiCard,
  Select,
  Table,
  Textarea,
  type BadgeTone,
  type SelectOption,
} from '../components/ui';
import type { Asset, AssetStatus, AssetType } from '@dsr/shared-types';
import './AssetsPage.css';

// ---------------------------------------------------------------------------
// Display maps
// ---------------------------------------------------------------------------

const TYPE_LABEL: Record<AssetType, string> = {
  radio: 'Radio',
  vehicle: 'Vehicle',
  firearm: 'Firearm',
  body_armor: 'Body armor',
  bodycam: 'Body camera',
  surveillance_kit: 'Surveillance kit',
  medical_kit: 'Medical kit',
  key: 'Key',
  access_card: 'Access card',
  phone: 'Phone',
  laptop: 'Laptop',
  drone: 'Drone',
  other: 'Other',
};

const TYPES: AssetType[] = [
  'radio', 'vehicle', 'firearm', 'body_armor', 'bodycam',
  'surveillance_kit', 'medical_kit', 'key', 'access_card',
  'phone', 'laptop', 'drone', 'other',
];

const STATUS_LABEL: Record<AssetStatus, string> = {
  available: 'Available',
  checked_out: 'Checked out',
  maintenance: 'Maintenance',
  retired: 'Retired',
  lost: 'Lost',
};

const STATUSES: AssetStatus[] = [
  'available', 'checked_out', 'maintenance', 'retired', 'lost',
];

// Asset status → the shared Badge tone (colors match the old as-badge-* set).
const STATUS_TONE: Record<AssetStatus, BadgeTone> = {
  available: 'success',
  checked_out: 'accent',
  maintenance: 'warn',
  retired: 'neutral',
  lost: 'danger',
};

// First field error from a zod 400 body, else a generic message.
function fieldError(err: unknown, fallback: string): string {
  if (err instanceof ApiError && err.status === 403) {
    return 'You do not have permission to do that.';
  }
  if (err instanceof ApiError && err.status === 400) {
    const body = err.body as
      | { details?: { fieldErrors?: Record<string, string[]> } }
      | null;
    const fe = body?.details?.fieldErrors;
    if (fe) {
      const first = Object.values(fe).find((a) => Array.isArray(a) && a.length > 0);
      if (first && Array.isArray(first)) return first[0]!;
    }
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// New-asset modal
// ---------------------------------------------------------------------------

function NewAssetModal({ onClose }: { onClose: () => void }): JSX.Element {
  const createAsset = useCreateAsset();
  const canFirearm = can('asset.firearm_write');

  const [name, setName] = useState('');
  const [assetType, setAssetType] = useState<AssetType>('radio');
  const [serial, setSerial] = useState('');
  const [makeModel, setMakeModel] = useState('');
  const [barcode, setBarcode] = useState('');
  const [status, setStatus] = useState<AssetStatus>('available');
  const [notes, setNotes] = useState('');
  const [error, setError] = useState<string | null>(null);

  // Hide the firearm option for users without firearm write — the API rejects
  // those creates anyway, so don't offer a path that 403s.
  const typeOptions = canFirearm ? TYPES : TYPES.filter((t) => t !== 'firearm');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (name.trim().length === 0) return;
    try {
      await createAsset.mutateAsync({
        name: name.trim(),
        asset_type: assetType,
        serial_number: serial.trim() || undefined,
        make_model: makeModel.trim() || undefined,
        barcode: barcode.trim() || undefined,
        status,
        notes: notes.trim() || undefined,
      });
      toast.success('Asset added to inventory.');
      onClose();
    } catch (err) {
      setError(fieldError(err, 'Could not save the asset. Try again.'));
    }
  };

  const busy = createAsset.isPending;

  return (
    <Dialog
      title="New asset"
      open
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="new-asset-form"
            variant="primary"
            disabled={busy || name.trim().length === 0}
          >
            {busy ? 'Saving…' : 'Save asset'}
          </Button>
        </>
      }
    >
      <form id="new-asset-form" className="as-form" onSubmit={onSubmit}>
        <FormField label="Name" required>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Motorola APX 6000, Ford Explorer #4"
            maxLength={200}
            disabled={busy}
            autoFocus
            required
          />
        </FormField>
        <FormRow>
          <FormField label="Type" required>
            <select
              className="form-input"
              value={assetType}
              onChange={(e) => setAssetType(e.target.value as AssetType)}
              disabled={busy}
            >
              {typeOptions.map((t) => (
                <option key={t} value={t}>{TYPE_LABEL[t]}</option>
              ))}
            </select>
          </FormField>
          <FormField label="Status">
            <select
              className="form-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as AssetStatus)}
              disabled={busy}
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>{STATUS_LABEL[s]}</option>
              ))}
            </select>
          </FormField>
        </FormRow>
        <FormRow>
          <FormField label="Serial number">
            <Input
              value={serial}
              onChange={(e) => setSerial(e.target.value)}
              maxLength={120}
              disabled={busy}
            />
          </FormField>
          <FormField label="Make / model">
            <Input
              value={makeModel}
              onChange={(e) => setMakeModel(e.target.value)}
              maxLength={200}
              disabled={busy}
            />
          </FormField>
        </FormRow>
        <FormField label="Barcode / asset tag">
          <Input
            value={barcode}
            onChange={(e) => setBarcode(e.target.value)}
            maxLength={200}
            disabled={busy}
          />
        </FormField>
        <FormField label="Notes">
          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={3}
            maxLength={10_000}
            disabled={busy}
            placeholder="Optional — accessories, caliber, calibration history, etc."
            style={{ resize: 'vertical', minHeight: 70 }}
          />
        </FormField>
        {error && <p className="as-error-banner" role="alert">{error}</p>}
      </form>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function AssetsPage(): JSX.Element {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<AssetType | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<AssetStatus | 'all'>('all');
  const [showNew, setShowNew] = useState(false);

  const filters: AssetFilters = useMemo(
    () => ({
      ...(typeFilter !== 'all' ? { asset_type: typeFilter } : {}),
      ...(statusFilter !== 'all' ? { status: statusFilter } : {}),
    }),
    [typeFilter, statusFilter],
  );

  const { data, isPending, error } = useAssets(filters);

  if (isPending) return <p className="as-status">Loading inventory…</p>;
  if (error) {
    const msg =
      error instanceof ApiError && error.status === 403
        ? 'You do not have permission to view assets.'
        : 'Failed to load assets.';
    return <p className="as-status as-error">{msg}</p>;
  }

  const assets: Asset[] = data?.assets ?? [];

  // Inventory roll-up by status (for the summary strip). Counted explicitly so
  // the values are plain numbers (not Record-indexed, which would be widened to
  // possibly-undefined under noUncheckedIndexedAccess).
  const countOf = (s: AssetStatus): number =>
    assets.filter((a) => a.status === s).length;
  const byStatus = {
    available: countOf('available'),
    checked_out: countOf('checked_out'),
    maintenance: countOf('maintenance'),
    retired: countOf('retired'),
    lost: countOf('lost'),
  };

  // Group the rows by asset type for a scannable inventory layout.
  const groups = TYPES
    .map((t) => ({ type: t, items: assets.filter((a) => a.asset_type === t) }))
    .filter((g) => g.items.length > 0);

  const typeFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All types' },
    ...TYPES.map((t) => ({ value: t, label: TYPE_LABEL[t] })),
  ];
  const statusFilterOptions: SelectOption[] = [
    { value: 'all', label: 'All statuses' },
    ...STATUSES.map((s) => ({ value: s, label: STATUS_LABEL[s] })),
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>Assets &amp; equipment</h1>
          <p className="page-sub">Inventory by type and custody status.</p>
        </div>
        {can('asset.write') && (
          <div className="page-toolbar">
            <Button variant="primary" onClick={() => setShowNew(true)}>
              New asset
            </Button>
          </div>
        )}
      </header>

      <div className="kpi-grid">
        <KpiCard label="Total" value={assets.length} />
        <KpiCard label="Available" value={byStatus.available} tone="success" />
        <KpiCard label="Checked out" value={byStatus.checked_out} />
        <KpiCard label="In maintenance" value={byStatus.maintenance} tone="warn" />
        <KpiCard label="Lost / retired" value={byStatus.lost + byStatus.retired} tone="danger" />
      </div>

      <div className="filters-bar">
        <div className="as-filter">
          <Select
            value={typeFilter}
            onValueChange={(v) => setTypeFilter(v as AssetType | 'all')}
            options={typeFilterOptions}
            aria-label="Filter by type"
          />
        </div>
        <div className="as-filter">
          <Select
            value={statusFilter}
            onValueChange={(v) => setStatusFilter(v as AssetStatus | 'all')}
            options={statusFilterOptions}
            aria-label="Filter by status"
          />
        </div>
      </div>

      {assets.length === 0 ? (
        <div className="as-empty">
          <p>No assets {typeFilter !== 'all' || statusFilter !== 'all' ? 'match these filters' : 'tracked yet'}.</p>
          {can('asset.write') && typeFilter === 'all' && statusFilter === 'all' && (
            <p className="as-empty-meta">Use “New asset” to add equipment.</p>
          )}
        </div>
      ) : (
        groups.map((g) => (
          <div key={g.type} className="as-group-block">
            <div className="as-group-head">
              <h3 className="as-group-title">{TYPE_LABEL[g.type]}</h3>
              <Badge tone="neutral">{g.items.length}</Badge>
            </div>
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.HeaderCell>Name</Table.HeaderCell>
                  <Table.HeaderCell>Serial</Table.HeaderCell>
                  <Table.HeaderCell>Make / model</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {g.items.map((a: Asset) => (
                  <Table.Row
                    key={a.id}
                    interactive
                    onClick={() => navigate(`/assets/${a.id}`)}
                    tabIndex={0}
                    role="link"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') navigate(`/assets/${a.id}`);
                    }}
                  >
                    <Table.Cell tone="strong">
                      {a.name}
                      {a.asset_type === 'firearm' && (
                        <Badge tone="danger" className="as-inline-flag">
                          controlled
                        </Badge>
                      )}
                    </Table.Cell>
                    <Table.Cell>{a.serial_number ? <code className="as-code">{a.serial_number}</code> : '—'}</Table.Cell>
                    <Table.Cell tone="muted">{a.make_model ?? '—'}</Table.Cell>
                    <Table.Cell>
                      <Badge tone={STATUS_TONE[a.status]}>{STATUS_LABEL[a.status]}</Badge>
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          </div>
        ))
      )}

      {showNew && <NewAssetModal onClose={() => setShowNew(false)} />}
    </section>
  );
}
